import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../styles.css";

import logo from "../images/ca_logo.png";

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [navTheme, setNavTheme] = useState(() => localStorage.getItem("arcdxNavTheme") || "dark");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout: authLogout } = useAuth();

  // Only main users can access Users section
  const isMainUser = user?.user_type === 'main';

  // Mobile bottom-navigation items (Users only for main users) — elegant outlined icons
  const bottomNavItems = [
    { label: "Clients", icon: <ReceiptLongOutlinedIcon />, path: "/" },
    { label: "Vendor", icon: <StorefrontOutlinedIcon />, path: "/vendor" },
    { label: "MIS Report", icon: <InsightsOutlinedIcon />, path: "/mis-report" },
    ...(isMainUser ? [{ label: "Users", icon: <ManageAccountsOutlinedIcon />, path: "/users" }] : []),
  ];
  const isNavActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  // Inside the Vendor section the floating pill becomes a contextual sub-nav
  // (Vendors / Payments / Approvals) — back chip + edge-swipe return to Clients.
  const inVendorSection = location.pathname.startsWith("/vendor");
  const vendorTab = new URLSearchParams(location.search).get("tab") || "payments";
  const hasVendorListAccess = isMainUser || user?.access_rights?.vendorListTab !== 'no_access';
  const hasVendorManagementAccess = isMainUser || user?.access_rights?.vendorManagementTab !== 'no_access';
  const hasApprovalMatrixAccess = isMainUser || user?.access_rights?.approvalMatrixTab !== 'no_access';
  // Tabs without access still show — frozen, for context — rather than
  // disappearing, so it's obvious there's more to this section than what's
  // currently unlocked.
  const vendorNavItems = [
    { label: "Vendors", icon: <StorefrontOutlinedIcon />, tabKey: "vendors", enabled: hasVendorListAccess },
    { label: "Payments", icon: <PaymentsOutlinedIcon />, tabKey: "payments", enabled: hasVendorManagementAccess },
    { label: "Approvals", icon: <FactCheckOutlinedIcon />, tabKey: "approvals", enabled: hasApprovalMatrixAccess },
  ];

  // iOS-style edge swipe (from the left edge, swiping right) → back to Clients
  useEffect(() => {
    if (!inVendorSection) return;
    let startX = null, startY = null;
    const onStart = (e) => {
      const t = e.touches && e.touches[0];
      if (t && t.clientX < 32) { startX = t.clientX; startY = t.clientY; } else { startX = null; }
    };
    const onEnd = (e) => {
      if (startX === null) return;
      const t = e.changedTouches && e.changedTouches[0];
      if (t && t.clientX - startX > 70 && Math.abs(t.clientY - startY) < 60) navigate("/");
      startX = null;
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [inVendorSection, navigate]);
  const pageTitle =
    { "/": "Clients", "/vendor": "Vendor", "/mis-report": "MIS Report", "/users": "Users" }[location.pathname] ||
    "ARCDX";

  const handleSettingsOpen = (event) => setSettingsAnchor(event.currentTarget);
  const handleSettingsClose = () => setSettingsAnchor(null);
  const selectNavTheme = (t) => {
    setNavTheme(t);
    localStorage.setItem("arcdxNavTheme", t);
    setSettingsAnchor(null);
  };

  // Bottom-nav palette: Luxury (Dark, gold/charcoal) vs Brand (Light, white/sky-blue)
  // NOTE: Dark theme parked for now — focusing on Light only. Restore the line below to re-enable.
  // const isDarkNav = navTheme === "dark";
  const isDarkNav = false;
  const navStyles = isDarkNav
    ? {
        bg: "rgba(24, 22, 20, 0.92)",
        border: "1px solid rgba(203, 163, 92, 0.18)",
        shadow: "0 18px 44px rgba(0, 0, 0, 0.38)",
        accent: "#CBA35C",
        inactive: "rgba(255, 255, 255, 0.55)",
        glow: "radial-gradient(circle, rgba(203,163,92,0.22) 0%, rgba(203,163,92,0) 72%)",
        dropShadow: "drop-shadow(0 3px 8px rgba(203,163,92,0.45))",
      }
    : {
        bg: "rgba(255, 255, 255, 0.9)",
        border: "1px solid rgba(57, 170, 252, 0.16)",
        shadow: "0 6px 20px rgba(57, 170, 252, 0.1)",
        accent: "#39aafc",
        inactive: "#98a2b3",
        glow: "radial-gradient(circle, rgba(57,170,252,0.22) 0%, rgba(57,170,252,0) 72%)",
        dropShadow: "drop-shadow(0 3px 8px rgba(57,170,252,0.40))",
      };

  // Matching header palette (covers the iPhone status-bar area too)
  const headerStyles = isDarkNav
    ? {
        bg: "linear-gradient(150deg, #221f1b 0%, #14110f 100%)",
        title: "#f6efe3",
        subtitle: "rgba(246, 239, 227, 0.6)",
        icon: "rgba(246, 239, 227, 0.85)",
        chip: "rgba(203, 163, 92, 0.14)",
        accent: "#CBA35C",
        border: "none",
        shadow: "0 12px 30px rgba(0, 0, 0, 0.34)",
        avatarBg: "#CBA35C",
        avatarColor: "#181614",
        avatarRing: "0 0 0 2px rgba(203, 163, 92, 0.5)",
        statusBar: "#14110f",
      }
    : {
        bg: "#ffffff",
        title: "#101828",
        subtitle: "#667085",
        icon: "#475467",
        chip: "#f2f4f7",
        accent: "#39aafc",
        border: "none",
        shadow: "0 8px 22px rgba(16, 24, 40, 0.12)",
        avatarBg: "#39aafc",
        avatarColor: "#ffffff",
        avatarRing: "0 0 0 2px rgba(57, 170, 252, 0.28)",
        statusBar: "#ffffff",
      };

  // Sync the status-bar tint AND the root/body background so the very top matches the header (no grey gap)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", headerStyles.statusBar);
    document.documentElement.style.backgroundColor = headerStyles.statusBar;
    document.body.style.backgroundColor = headerStyles.statusBar;
  }, [headerStyles.statusBar]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="app-shell">
      <div className="sidebar">
        <div className="logo-container">
          <img src={logo} alt="Company Logo" className="sidebar-logo" />
        </div>

        <div className="icon-list-holder">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive || window.location.pathname === "/" ? "icon-item icon-item-active" : "icon-item"
            }
          >
            <ReceiptLongOutlinedIcon />
            <Typography variant="body1">
              Clients
            </Typography>
          </NavLink>

          <NavLink
            to="/vendor"
            className={({ isActive }) =>
              isActive ? "icon-item icon-item-active" : "icon-item"
            }
          >
            <StorefrontOutlinedIcon />
            <Typography variant="body1">
              Vendor
            </Typography>
          </NavLink>

          <NavLink
            to="/mis-report"
            className={({ isActive }) =>
              isActive ? "icon-item icon-item-active" : "icon-item"
            }
          >
            <InsightsOutlinedIcon />
            <Typography variant="body1">
              MIS Report
            </Typography>
          </NavLink>

          {isMainUser && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                isActive ? "icon-item icon-item-active" : "icon-item"
              }
            >
              <ManageAccountsOutlinedIcon />
              <Typography variant="body1">
                Users
              </Typography>
            </NavLink>
          )}
        </div>

        {/* Signed-in card pinned to the rail's bottom (desktop only — the whole
            .sidebar is display:none under 900px). Clicking it opens the same
            Profile/Logout menu the top bar used to trigger. */}
        <Box
          onClick={handleMenuOpen}
          sx={{
            mt: "auto",
            mb: "10px",
            mx: "14px",
            p: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            borderRadius: "14px",
            border: "1px solid #e9eef5",
            background: "#fafcff",
            minWidth: 0,
            cursor: "pointer",
            transition: "background 0.18s ease, border-color 0.18s ease",
            "&:hover": { background: "#f2f6fb", borderColor: "#dce7f5" },
          }}
        >
          <Avatar
            sx={{
              background: "linear-gradient(135deg, #4db2fc, #1565C0)",
              width: 34,
              height: 34,
              fontSize: "13px",
              fontWeight: 700,
            }}
          >
            {user && user.fullname
              ? user.fullname.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()
              : "U"}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: "12.5px !important", fontWeight: 700, color: "#101828", lineHeight: 1.25 }}>
              {user && user.fullname ? user.fullname : "User"}
            </Typography>
            <Typography noWrap sx={{ fontSize: "11px !important", color: "#9aa7b8", lineHeight: 1.3 }}>
              {user && user.companyname ? user.companyname : "Workspace"}
            </Typography>
          </Box>
        </Box>

        {/* Profile / Logout menu — anchored to the sidebar card above, opens upward */}
        {!isMobile && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
              sx: {
                width: "200px",
                borderRadius: "14px",
                mb: 1,
                ml: "14px",
                border: "1px solid #eef2f6",
                boxShadow: "0 12px 32px rgba(16, 24, 40, 0.14)",
              },
            }}
          >
            <MenuItem onClick={handleMenuClose} sx={{ fontSize: "13.5px", fontWeight: 600, color: "#101828", py: 1.2 }}>
              {user && user.fullname ? user.fullname : "Profile"}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                handleMenuClose();
                await authLogout();
                navigate("/login");
              }}
              sx={{ color: "#d92d20", fontSize: "13.5px", fontWeight: 700, py: 1.2 }}
            >
              <LogoutIcon sx={{ fontSize: 18, mr: 1.25 }} />
              Logout
            </MenuItem>
          </Menu>
        )}
      </div>

      {/* Mobile Header — theme-aware, covers the iPhone status-bar area */}
      <Box
        className="arcdx-mobile-header"
        sx={{
          display: { xs: "flex", md: "none" },
          position: "sticky",
          top: 0,
          zIndex: 1100,
          flexShrink: 0,
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          background: headerStyles.bg,
          px: 2.5,
          // keep the row near the top; give the header more body below it
          pt: "max(18px, env(safe-area-inset-top))",
          pb: 3.5,
          borderBottomLeftRadius: "28px",
          borderBottomRightRadius: "28px",
          borderBottom: "1px solid rgba(57, 170, 252, 0.16)",
          boxShadow: "0 3px 12px rgba(57, 170, 252, 0.09)",
          transition: "background 0.35s ease, box-shadow 0.35s ease",
        }}
      >
        {inVendorSection && (
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              width: 38,
              height: 38,
              mr: 0.5,
              flexShrink: 0,
              color: headerStyles.icon,
              background: headerStyles.chip,
              "&:active": { transform: "scale(0.9)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 21 }} />
          </IconButton>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "21px",
              fontWeight: 700,
              color: headerStyles.title,
              fontFamily: "Roboto, sans-serif",
              lineHeight: 1.2,
              letterSpacing: isDarkNav ? "0.015em" : 0,
              transition: "color 0.35s ease",
            }}
          >
            {pageTitle}
          </Typography>
          <Typography noWrap sx={{ fontSize: "12px", color: headerStyles.subtitle, fontFamily: "Roboto, sans-serif", mt: "2px", transition: "color 0.35s ease" }}>
            {user && user.companyname ? user.companyname : "Your Company"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            sx={{
              width: 38,
              height: 38,
              color: headerStyles.icon,
              background: headerStyles.chip,
              position: "relative",
              transition: "transform 0.15s ease, background 0.35s ease, color 0.35s ease",
              "&:hover": { background: headerStyles.chip },
              "&:active": { transform: "scale(0.9)" },
            }}
          >
            <NotificationsNoneIcon sx={{ fontSize: 21 }} />
            <Box sx={{ position: "absolute", top: "8px", right: "9px", width: "8px", height: "8px", borderRadius: "50%", background: "#f04438", border: `2px solid ${headerStyles.statusBar}` }} />
          </IconButton>
          {/* Settings icon (Dark/Light theme toggle) parked for now — focusing on Light only:
          <IconButton
            onClick={handleSettingsOpen}
            sx={{
              width: 38,
              height: 38,
              color: settingsAnchor ? headerStyles.accent : headerStyles.icon,
              background: headerStyles.chip,
              transition: "transform 0.15s ease, background 0.35s ease, color 0.2s ease",
              "&:hover": { background: headerStyles.chip },
              "&:active": { transform: "scale(0.9)" },
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 21 }} />
          </IconButton>
          */}
          <IconButton onClick={handleMenuOpen} sx={{ p: "2px" }}>
            <Avatar
              sx={{
                bgcolor: headerStyles.avatarBg,
                color: headerStyles.avatarColor,
                fontWeight: 700,
                width: 38,
                height: 38,
                fontSize: "15px",
                boxShadow: headerStyles.avatarRing,
                transition: "background 0.35s ease",
              }}
            >
              {user && user.fullname
                ? user.fullname
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase()
                : "U"}
            </Avatar>
          </IconButton>
        </Box>
      </Box>

      {/* Appearance (theme) menu parked for now — Dark/Light toggle to be restored later:
      <Menu
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={handleSettingsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 220, borderRadius: "14px", mt: 0.5, boxShadow: "0 12px 32px rgba(16,24,40,0.16)" } }}
      >
        <Typography sx={{ px: 2, pt: 1.25, pb: 0.5, fontSize: "11px", fontWeight: 700, color: "#98a2b3", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Appearance
        </Typography>
        <MenuItem onClick={() => selectNavTheme("dark")} sx={{ fontSize: "14px", py: 1.1 }}>
          <Box sx={{ width: 18, height: 18, borderRadius: "5px", background: "linear-gradient(135deg, #181614 45%, #CBA35C 100%)", mr: 1.5, border: "1px solid #e0e0e0" }} />
          Luxury (Dark)
          {navTheme === "dark" && <CheckIcon sx={{ fontSize: 18, ml: "auto", color: "#39aafc" }} />}
        </MenuItem>
        <MenuItem onClick={() => selectNavTheme("light")} sx={{ fontSize: "14px", py: 1.1 }}>
          <Box sx={{ width: 18, height: 18, borderRadius: "5px", background: "linear-gradient(135deg, #ffffff 45%, #39aafc 100%)", mr: 1.5, border: "1px solid #e0e0e0" }} />
          Brand (Light)
          {navTheme === "light" && <CheckIcon sx={{ fontSize: 18, ml: "auto", color: "#39aafc" }} />}
        </MenuItem>
      </Menu>
      */}

      {/* Profile / Logout — mobile bottom sheet, opened from the header avatar */}
      {isMobile && Boolean(anchorEl) && (
        <>
          <div className="arcdx-sheet-back" onClick={handleMenuClose} />
          <div className="arcdx-sheet">
            <div className="arcdx-sheet-grip" />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 2, mb: 1, borderBottom: "1px solid #f2f4f7" }}>
              <Avatar sx={{ bgcolor: "#39aafc", width: 48, height: 48, fontWeight: 700, fontSize: "17px" }}>
                {user && user.fullname
                  ? user.fullname.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()
                  : "U"}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: "16px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
                  {user && user.fullname ? user.fullname : "Profile"}
                </Typography>
                {user?.companyname && (
                  <Typography noWrap sx={{ fontSize: "12.5px", color: "#98a2b3", fontFamily: "Roboto, Arial, sans-serif" }}>
                    {user.companyname}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              onClick={async () => {
                handleMenuClose();
                await authLogout();
                navigate("/login");
              }}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.4, px: 1, borderRadius: "14px", cursor: "pointer", color: "#d92d20", "&:active": { background: "#fef3f2" } }}
            >
              <LogoutIcon sx={{ fontSize: 21 }} />
              <Typography sx={{ fontSize: "15px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif" }}>Logout</Typography>
            </Box>
          </div>
        </>
      )}

      <div className="main-content">
        {children}
      </div>

      {/* Mobile Bottom Navigation — rebuilt clean-room on the MIS-World .mc-tabbar
          pattern: a plain <nav>, styled entirely in styles.css, last flex child of
          the fixed .app-shell so the layout engine pins it to the screen bottom. */}
      <nav className="arcdx-tabbar" id="arcdx-tabbar">
        {(inVendorSection ? vendorNavItems : bottomNavItems).map((item) => {
          const active = item.tabKey ? vendorTab === item.tabKey : isNavActive(item.path);
          const enabled = item.enabled !== false;
          return (
            <button
              key={item.tabKey || item.path}
              type="button"
              className={`arcdx-tab${active && enabled ? " active" : ""}${enabled ? "" : " frozen"}`}
              onClick={() => enabled && (item.tabKey ? navigate(`/vendor?tab=${item.tabKey}`, { replace: true }) : navigate(item.path))}
            >
              <span className="arcdx-tab-ico">
                {React.cloneElement(item.icon, { sx: { fontSize: 24 } })}
              </span>
              <span className="arcdx-tab-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
